import { cva } from 'class-variance-authority';

/**
 * Input type variants
 */
export const INPUT_TYPE = {
  DEFAULT: 'default',
  LEADING_ICON: 'leading-icon',
  TRAILING_DROPDOWN: 'trailing-dropdown'
} as const;

export type InputType = (typeof INPUT_TYPE)[keyof typeof INPUT_TYPE];

/**
 * Input state variants
 */
export const INPUT_STATE = {
  DEFAULT: 'default',
  FILLED: 'filled',
  FOCUSED: 'focused',
  FILLED_FOCUSED: 'filled-focused',
  DISABLED: 'disabled'
} as const;

export type InputState = (typeof INPUT_STATE)[keyof typeof INPUT_STATE];

/**
 * Container wrapper variants (includes label and hint text)
 */
export const inputContainerVariants = cva(['flex', 'flex-col', 'min-w-0'], {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: ''
    }
  },
  defaultVariants: {
    fullWidth: true
  }
});

/**
 * Label variants
 */
export const inputLabelVariants = cva(
  [
    'text-primary', // #1A2022
    'font-medium',
    'text-sm', // 14px
    'leading-xs' // 16px - line-height for vertical centering
  ],
  {
    variants: {
      disabled: {
        true: 'text-disabled', // #98A2B3
        false: ''
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);

/**
 * Optional text variants
 */
export const inputOptionalTextVariants = cva(
  [
    'text-secondary', // #475467
    'font-normal',
    'text-xs', // 12px
    'leading-xs' // 18px
  ],
  {
    variants: {},
    defaultVariants: {}
  }
);

/**
 * Input field container variants
 */
export const inputFieldContainerVariants = cva(
  [
    'flex',
    'items-center',
    'gap-3xs', // 6px - gap between icon and text
    'h-6xl', // 36px
    'rounded-sm', // 6px
    'border',
    'bg-base-white',
    'transition-colors',
    'py-3xs', // 6px
    'px-s' // 12px
  ],
  {
    variants: {
      state: {
        [INPUT_STATE.DEFAULT]: ['border-primary'], // #D0D5DD
        [INPUT_STATE.FILLED]: ['border-primary'],
        [INPUT_STATE.FOCUSED]: [
          'border-focus' // #0596FF
        ],
        [INPUT_STATE.FILLED_FOCUSED]: [
          'border-focus'
        ],
        [INPUT_STATE.DISABLED]: [
          'bg-disabled', // #F2F4F7
          'border-disabled', // #DEE3EA
          'cursor-not-allowed'
        ]
      },
      error: {
        true: [
          'border-negative', // #F04438
          'focus-within:border-negative'
        ],
        false: ''
      }
    },
    compoundVariants: [
      {
        state: INPUT_STATE.DISABLED,
        error: true,
        class: 'border-disabled' // disabled takes precedence
      }
    ],
    defaultVariants: {
      state: 'default',
      error: false
    }
  }
);

/**
 * Icon wrapper variants (for leading icon)
 */
export const inputIconWrapperVariants = cva(
  [
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    '[&_svg]:size-l', // 16px
    '[&_svg]:stroke-[2]'
  ],
  {
    variants: {
      disabled: {
        true: 'text-tertiary', // #98A2B3
        false: 'text-secondary' // #475467
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);

/**
 * Input text field variants
 */
export const inputTextFieldVariants = cva(
  [
    'flex-1',
    'outline-none',
    'bg-transparent',
    'border-none',
    'font-body',
    'text-sm', // 14px
    'leading-md', // 22px
    'font-normal',
    'w-full',
    'min-w-0', // Allow text to shrink
    'placeholder:font-body'
  ],
  {
    variants: {
      state: {
        [INPUT_STATE.DEFAULT]: [
          'text-primary', // #1A2022
          'placeholder:text-tertiary' // #98A2B3
        ],
        [INPUT_STATE.FILLED]: ['text-primary'],
        [INPUT_STATE.FOCUSED]: [
          'text-primary',
          'placeholder:text-tertiary'
        ],
        [INPUT_STATE.FILLED_FOCUSED]: ['text-primary'],
        [INPUT_STATE.DISABLED]: [
          'text-disabled', // #98A2B3
          'cursor-not-allowed',
          'placeholder:text-disabled'
        ]
      }
    },
    defaultVariants: {
      state: 'default'
    }
  }
);

/**
 * Clear icon (X icon) variants - just icon, not a button
 */
export const inputClearButtonVariants = cva(
  [
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'flex-shrink-0',
    '[&_svg]:size-l', // 16px x 16px
    '[&_svg]:stroke-[2]'
  ],
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed text-tertiary',
        false: 'text-secondary'
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);

/**
 * Hint text container variants
 */
export const inputHintTextVariants = cva(
  [
    'flex',
    'gap-4xs', // 4px
    'mt-3xs', // 6px
    'w-0',
    'min-w-full', // prevent hint text from expanding the flex-col container
    'text-xs', // 12px
    'leading-xs', // 18px
    'font-normal',
    'break-words',
    'overflow-hidden'
  ],
  {
    variants: {
      error: {
        true: 'text-negative', // #F04438
        false: 'text-secondary' // #475467
      }
    },
    defaultVariants: {
      error: false
    }
  }
);

/**
 * Info icon wrapper variants
 */
export const inputInfoIconVariants = cva(
  [
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    '[&_svg]:size-s' // 12px
  ],
  {
    variants: {},
    defaultVariants: {}
  }
);

/**
 * Dropdown button wrapper (for trailing-dropdown type)
 * Matches Button component style from Figma
 */
export const inputDropdownButtonVariants = cva(
  [
    'flex',
    'items-center',
    'justify-center',
    'gap-3xs', // 6px between text and icon
    'flex-shrink-0',
    'text-sm', // 14px
    'leading-[20px]',
    'font-semibold',
    'text-primary'
  ],
  {
    variants: {
      disabled: {
        true: 'text-tertiary cursor-not-allowed',
        false: 'text-primary cursor-pointer'
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);

// ─── Textarea variants ────────────────────────────────────────────────────────

/**
 * Textarea state variants
 */
export const TEXTAREA_STATE = {
  DEFAULT: 'default',
  FILLED: 'filled',
  FOCUSED: 'focused',
  FILLED_FOCUSED: 'filled-focused',
  DISABLED: 'disabled'
} as const;

export type TextareaState = (typeof TEXTAREA_STATE)[keyof typeof TEXTAREA_STATE];

/**
 * Textarea field container variants (border + background)
 */
export const textareaFieldContainerVariants = cva(
  ['rounded-sm', 'border', 'bg-base-white', 'transition-colors', 'overflow-hidden'],
  {
    variants: {
      state: {
        [TEXTAREA_STATE.DEFAULT]: 'border-primary',
        [TEXTAREA_STATE.FILLED]: 'border-primary',
        [TEXTAREA_STATE.FOCUSED]: 'border-focus',
        [TEXTAREA_STATE.FILLED_FOCUSED]: 'border-focus',
        [TEXTAREA_STATE.DISABLED]: ['bg-disabled', 'border-disabled', 'cursor-not-allowed']
      },
      error: {
        true: 'border-negative',
        false: ''
      }
    },
    compoundVariants: [
      {
        state: TEXTAREA_STATE.DISABLED,
        error: true,
        class: 'border-disabled'
      }
    ],
    defaultVariants: {
      state: 'default',
      error: false
    }
  }
);

/**
 * Textarea text field variants
 */
export const textareaTextFieldVariants = cva(
  [
    'w-full',
    'block',
    'outline-none',
    'bg-transparent',
    'border-none',
    'font-body',
    'text-sm',
    'leading-m',
    'font-normal',
    'placeholder:font-body',
    'min-w-0',
    'px-m',
    'py-xs'
  ],
  {
    variants: {
      state: {
        [TEXTAREA_STATE.DEFAULT]: ['text-primary', 'placeholder:text-tertiary'],
        [TEXTAREA_STATE.FILLED]: 'text-primary',
        [TEXTAREA_STATE.FOCUSED]: ['text-primary', 'placeholder:text-tertiary'],
        [TEXTAREA_STATE.FILLED_FOCUSED]: 'text-primary',
        [TEXTAREA_STATE.DISABLED]: [
          'text-disabled',
          'cursor-not-allowed',
          'placeholder:text-disabled'
        ]
      }
    },
    defaultVariants: { state: 'default' }
  }
);
